/* ============================================================
   Wido Ops — browser push (Web Push) enrollment. Registers the
   service worker, subscribes via the PushManager using the
   server's VAPID public key, and syncs the subscription to the
   backend. Works on Chrome, Firefox and Safari 16+ (HTTPS or
   localhost). Exposes window.WidoPush.
   ============================================================ */
(function () {
  const API = () => window.WidoApi;

  function supported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  }

  let swReg = null;
  async function register() {
    if (!supported()) return null;
    if (swReg) return swReg;
    try { swReg = await navigator.serviceWorker.register("/sw.js"); return swReg; }
    catch (e) { return null; }
  }

  // base64url (VAPID public key) -> Uint8Array for applicationServerKey.
  function urlB64ToUint8Array(base64) {
    const pad = "=".repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + pad).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(b64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
  }

  async function status() {
    if (!supported()) return { supported: false };
    let subscribed = false;
    try {
      const reg = await register();
      subscribed = !!(reg && (await reg.pushManager.getSubscription()));
    } catch (e) {}
    return { supported: true, permission: Notification.permission, subscribed };
  }

  function sameKey(a, b) {
    if (!a || !b) return false;
    const ua = new Uint8Array(a), ub = new Uint8Array(b);
    if (ua.length !== ub.length) return false;
    for (let i = 0; i < ua.length; i++) if (ua[i] !== ub[i]) return false;
    return true;
  }

  // Get a subscription for `publicKey`, DROPPING any existing one made under a
  // different VAPID key (a rotated key would otherwise leave an undeliverable,
  // never-pruned subscription).
  async function subscribeWith(reg, publicKey) {
    const want = urlB64ToUint8Array(publicKey);
    let sub = await reg.pushManager.getSubscription();
    if (sub && !sameKey(sub.options && sub.options.applicationServerKey, want)) {
      try { await sub.unsubscribe(); } catch (e) {}
      sub = null;
    }
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: want });
    return sub;
  }

  async function enable() {
    if (!supported()) return { ok: false, reason: "unsupported" };
    const cfg = await API().pushConfig().catch(() => null);
    if (!cfg || !cfg.enabled || !cfg.publicKey) return { ok: false, reason: "server_off" };
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return { ok: false, reason: perm === "denied" ? "denied" : "dismissed" };
    const reg = await register();
    if (!reg) return { ok: false, reason: "no_sw" };
    const sub = await subscribeWith(reg, cfg.publicKey);
    const j = sub.toJSON();
    await API().pushSubscribe({ endpoint: j.endpoint, keys: j.keys });
    return { ok: true };
  }

  // Re-bind an existing browser subscription to the CURRENT logged-in user
  // (called on every load): a shared machine must never keep delivering the
  // PREVIOUS user's notifications. No-op unless already granted + subscribed.
  async function resync() {
    try {
      if (!supported() || Notification.permission !== "granted") return;
      const cfg = await API().pushConfig().catch(() => null);
      if (!cfg || !cfg.enabled || !cfg.publicKey) return;
      const reg = await register();
      if (!reg || !(await reg.pushManager.getSubscription())) return;
      const sub = await subscribeWith(reg, cfg.publicKey);
      const j = sub.toJSON();
      await API().pushSubscribe({ endpoint: j.endpoint, keys: j.keys });
    } catch (e) {}
  }

  async function disable() {
    try {
      const reg = await register();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await API().pushUnsubscribe({ endpoint }).catch(() => {});
      }
      return { ok: true };
    } catch (e) { return { ok: false }; }
  }

  async function test() { try { return await API().pushTest(); } catch (e) { return null; } }

  window.WidoPush = { supported, register, status, enable, disable, resync, test };
})();
